/**
 * Matches an Instagram-style mention: `@` followed by letters, digits,
 * underscores and dots. Resolution downstream is case-insensitive.
 * A trailing `.` is treated as sentence punctuation and stripped, because
 * Instagram usernames cannot end with a period.
 */
const MENTION_REGEX = /@([A-Za-z0-9._]+)/g;
function createMentionNode(handle) {
    return {
        type: 'creatorMention',
        data: {
            hName: 'creator-mention',
            hProperties: { handle },
        },
    };
}
/**
 * Split a single text node's value into a sequence of text + mention nodes.
 * Returns `null` when the text contains no mention (so the caller can leave
 * the original node untouched and avoid needless tree churn).
 */
function splitTextNode(value) {
    const replacements = [];
    let lastIndex = 0;
    let matched = false;
    MENTION_REGEX.lastIndex = 0;
    let match;
    while ((match = MENTION_REGEX.exec(value)) !== null) {
        let handle = match[1];
        let matchEnd = match.index + match[0].length;
        // Strip a trailing period: it is sentence punctuation, never part of an
        // Instagram username. Push the period back into the following text run.
        if (handle.endsWith('.')) {
            handle = handle.slice(0, -1);
            matchEnd -= 1;
        }
        // A bare "@" (e.g. "@.") leaves an empty handle — not a mention.
        if (handle.length === 0) {
            continue;
        }
        matched = true;
        const preceding = value.slice(lastIndex, match.index);
        if (preceding) {
            replacements.push({ type: 'text', value: preceding });
        }
        replacements.push(createMentionNode(handle));
        lastIndex = matchEnd;
    }
    if (!matched) {
        return null;
    }
    const trailing = value.slice(lastIndex);
    if (trailing) {
        replacements.push({ type: 'text', value: trailing });
    }
    return replacements;
}
/**
 * Depth-first walk of a parent's children, replacing `@handle` runs inside
 * text nodes with `creator-mention` nodes. `code` / `inlineCode` are skipped
 * so handles inside code stay literal. Inserted nodes are stepped over (the
 * text slices around a mention never contain a further mention).
 */
function transformChildren(parent) {
    for (let i = 0; i < parent.children.length; i++) {
        const child = parent.children[i];
        if (child.type === 'code' || child.type === 'inlineCode') {
            continue;
        }
        if (child.type === 'text' && typeof child.value === 'string') {
            const replacements = splitTextNode(child.value);
            if (replacements) {
                parent.children.splice(i, 1, ...replacements);
                i += replacements.length - 1;
            }
            continue;
        }
        if (Array.isArray(child.children)) {
            transformChildren(child);
        }
    }
}
/**
 * remark plugin: turns `@handle` occurrences in markdown text into custom
 * `creator-mention` nodes so a ReactMarkdown consumer can render them as
 * inline chips. Text inside `code` and `inlineCode` is left untouched.
 *
 * The plugin is pure: it mutates the tree it is given and returns nothing,
 * matching the remark transformer signature `(tree) => void`.
 */
export function remarkCreatorMentions() {
    return (tree) => {
        transformChildren(tree);
    };
}
export default remarkCreatorMentions;
